import * as Tabs from "@radix-ui/react-tabs";
import IconTip from "components/Dashboard/PeoplePanel/IconTip";
import { createContext, ReactNode, useContext, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import ActivityTab, { ActivityView } from "./ActivityTab";
import AutomationsTab from "./AutomationsTab";
import ConnectedAppsTab from "./ConnectedAppsTab";
import { AutomationsProvider, LoadingRows, useApprovals } from "./hooks";

export type AutomationsTabName = "automations" | "activity" | "apps";

const TABS: { value: AutomationsTabName; label: string }[] = [
  { value: "automations", label: "Automations" },
  { value: "activity", label: "Activity" },
  { value: "apps", label: "Connected apps" },
];

function tabOf(param: string | null): AutomationsTabName {
  return param === "activity" || param === "apps" ? param : "automations";
}

const HeaderSlot = createContext<HTMLElement | null>(null);

/**
 * Buttons for the page's header, at the right of the Automations title
 * (like Message Templates' New template). A tab renders them where it
 * likes; they show in the header.
 */
export function HeaderActions({ children }: { children: ReactNode }) {
  const slot = useContext(HeaderSlot);
  return slot ? createPortal(children, slot) : null;
}

/** The address's parameters that belong to one tab: they don't follow to another. */
const TAB_PARAMS: Record<AutomationsTabName, string[]> = {
  automations: ["open", "template"],
  activity: ["view", "automation"],
  apps: [],
};

/**
 * Where a tab sends people: another tab (and Activity's list), in the
 * address. Going elsewhere drops the parameters of the tab it leaves
 * (?open=, ?template=, ?view=) and keeps any other.
 */
export function useAutomationsNav() {
  const [params, setParams] = useSearchParams();
  return {
    tab: tabOf(params.get("tab")),
    view: (params.get("view") === "done" ? "done" : "waiting") as ActivityView,
    go: (tab: AutomationsTabName, view?: ActivityView) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const from = tabOf(prev.get("tab"));
          if (from !== tab) TAB_PARAMS[from].forEach((key) => next.delete(key));
          if (tab === "automations") next.delete("tab");
          else next.set("tab", tab);
          if (tab === "activity" && view === "done") next.set("view", "done");
          else next.delete("view");
          return next;
        },
        { replace: true }
      ),
  };
}

function Title({ slot }: { slot?: (el: HTMLElement | null) => void }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h2 className="text-xl font-bold">Automations</h2>
      <span ref={slot} className="inline-flex flex-wrap gap-2" />
    </div>
  );
}

function Shell() {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const { tab, view, go } = useAutomationsNav();
  const { approvals } = useApprovals();
  const waiting = approvals.length;

  return (
    <HeaderSlot.Provider value={slot}>
      <Title slot={setSlot} />
      <Tabs.Root value={tab} onValueChange={(next) => go(tabOf(next))}>
        <Tabs.List
          className="mt-5 flex flex-wrap gap-x-7 border-b border-gray-200"
          aria-label="Automations"
        >
          {TABS.map(({ value, label }) => (
            <Tabs.Trigger
              key={value}
              value={value}
              className="-mb-px flex items-center gap-2 whitespace-nowrap border-b-[3px] border-transparent pb-2.5 text-[15px] font-medium text-gray-400 hover:text-gray-800 data-[state=active]:border-green-500 data-[state=active]:font-semibold data-[state=active]:text-neutral-900"
            >
              {label}
              {value === "activity" && waiting > 0 && (
                <IconTip
                  label={
                    waiting === 1
                      ? "1 step is waiting for approval."
                      : `${waiting} steps are waiting for approval.`
                  }
                >
                  <span className="rounded-full bg-orange-500 px-2 text-xs font-semibold leading-5 text-white">
                    {waiting > 99 ? "99+" : waiting}
                  </span>
                </IconTip>
              )}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
      </Tabs.Root>
      <div className="mt-5">
        {tab === "automations" && <AutomationsTab />}
        {tab === "activity" && (
          <ActivityTab view={view} onView={(next) => go("activity", next)} />
        )}
        {tab === "apps" && <ConnectedAppsTab />}
      </div>
    </HeaderSlot.Provider>
  );
}

/**
 * The Automations area: the title, and the tabs Automations, Activity
 * (Waiting for approval and Done) and Connected apps. The tab is in the
 * address (?tab=activity&view=done), so a link can open any of them.
 */
export default function AutomationsPage({ orgId }: { orgId: number }) {
  return (
    <div className="py-6 px-7 rounded-xl bg-neutral-50">
      <AutomationsProvider
        orgId={orgId}
        fresh
        loading={
          <>
            <Title />
            <LoadingRows />
          </>
        }
        failed={(reason) => (
          <>
            <Title />
            <p className="mt-6 text-sm text-red-600">{reason}</p>
          </>
        )}
      >
        <Shell />
      </AutomationsProvider>
    </div>
  );
}
